import React, { useState, useEffect } from 'react';
import {
  generatePredictionsByDate,
  getReports,
  ReportItem
} from '../api';
import { toast } from 'react-toastify';
import { commonStyles, colors, typography, spacing } from '../styles/design-system';
import { User } from '../api/auth';
import { useAdminList } from '../hooks/useAdminList';

interface PredictionManagerProps {
  currentUser: User;
}

const PredictionManager: React.FC<PredictionManagerProps> = ({ currentUser }) => {
  const isAdministrator = currentUser.role === 'administrator';

  // 폼 상태 - 예측 생성용
  const [genDate, setGenDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [genSport, setGenSport] = useState<string>('');
  const [genAiProvider, setGenAiProvider] = useState<string>('gemini');
  const [targetAdminId, setTargetAdminId] = useState<number | undefined>(currentUser.admin_id);
  const [notionAdminId, setNotionAdminId] = useState<number | undefined>(currentUser.admin_id);

  // 필터 상태 - 내역 조회용 (생성과 독립)
  const [filterSport, setFilterSport] = useState<string>('');
  const [filterAiModel, setFilterAiModel] = useState<string>('');
  const [filterAdminId, setFilterAdminId] = useState<number | undefined>(undefined);
  const [showAllAdmins, setShowAllAdmins] = useState<boolean>(true);

  // 데이터 상태 (useAdminList 훅으로 관리자 목록 자동 조회)
  const { adminList } = useAdminList({ immediate: isAdministrator });
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [reportsOffset, setReportsOffset] = useState(0);
  const [reportsLimit] = useState(10);

  // 로딩 상태
  const [generating, setGenerating] = useState<boolean>(false);
  const [loadingReports, setLoadingReports] = useState<boolean>(false);

  // 전체 리포트 보기 모달
  const [selectedReport, setSelectedReport] = useState<ReportItem | null>(null);

  useEffect(() => {
    loadReports();
  }, [isAdministrator]);

  useEffect(() => {
    loadReports();
  }, [filterSport, filterAdminId, filterAiModel, reportsOffset, showAllAdmins]);

  const loadReports = async () => {
    setLoadingReports(true);
    try {
      // 총괄 관리자가 '전체' 선택 시 admin_id 전달 제외
      const adminIdToQuery = isAdministrator
        ? (showAllAdmins ? undefined : filterAdminId)
        : currentUser.admin_id;

      const response = await getReports({
        // 날짜 필터 없이 전체 조회
        sport: filterSport || undefined,
        admin_id: adminIdToQuery,
        ai_model: filterAiModel || undefined,
        limit: reportsLimit,
        offset: reportsOffset
      });
      setReports(response.data);
      setReportsTotal(response.total);
    } catch (error) {
      console.error('리포트 조회 실패:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  const handleGeneratePredictions = async () => {
    if (!genDate) {
      toast.warning('날짜를 선택해.');
      return;
    }

    setGenerating(true);

    try {
      const sports = genSport ? [genSport] : ['soccer', 'baseball', 'basketball', 'volleyball', 'hockey'];
      let totalSuccess = 0, totalFailed = 0;

      for (const sport of sports) {
        try {
          const response = await generatePredictionsByDate(
            sport,
            genDate.replace(/-/g, ''),
            genAiProvider,
            isAdministrator ? targetAdminId : undefined,
            false,
            isAdministrator ? notionAdminId : undefined
          );
          if (response.results) {
            totalSuccess += response.results.success || 0;
            totalFailed += response.results.failed || 0;
          }
        } catch (err) {
          console.error(`${sport} 처리 실패:`, err);
        }
      }

      if (totalSuccess === 0 && totalFailed === 0) {
        toast.info('해당 날짜에 경기가 없음.');
      } else {
        toast.success(`생성 완료: 성공 ${totalSuccess}, 실패 ${totalFailed}`);
        loadReports();
      }
    } catch (error: any) {
      toast.error(`생성 실패: ${error.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const getSportLabel = (sport: string): string => {
    const labels: Record<string, string> = { soccer: '축구', baseball: '야구', basketball: '농구', volleyball: '배구', hockey: '하키' };
    return labels[sport] || sport;
  };

  const formatMatchDate = (dateStr: string): string => {
    if (!dateStr || dateStr.length !== 8) return dateStr;
    return `${dateStr.slice(4, 6)}/${dateStr.slice(6, 8)}`;
  };

  const totalPages = Math.ceil(reportsTotal / reportsLimit);
  const currentPage = Math.floor(reportsOffset / reportsLimit) + 1;

  return (
    <div style={styles.container}>
      {/* Request Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>리포트 생성</h3>
        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>날짜</label>
            <input
              type="date"
              style={styles.input}
              value={genDate}
              onChange={(e) => setGenDate(e.target.value)}
              disabled={generating}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>종목</label>
            <select
              style={styles.select}
              value={genSport}
              onChange={(e) => setGenSport(e.target.value)}
              disabled={generating}
            >
              <option value="">전체</option>
              <option value="soccer">축구</option>
              <option value="baseball">야구</option>
              <option value="basketball">농구</option>
              <option value="volleyball">배구</option>
              <option value="hockey">하키</option>
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>AI 모델</label>
            <select
              style={styles.select}
              value={genAiProvider}
              onChange={(e) => setGenAiProvider(e.target.value)}
              disabled={generating}
            >
              <option value="gemini">Gemini</option>
              <option value="claude">Claude</option>
              <option value="openai">OpenAI</option>
            </select>
          </div>

          {isAdministrator && adminList.length > 0 && (
            <>
              <div style={styles.formGroup}>
                <label style={styles.label}>프롬프트</label>
                <select
                  style={styles.select}
                  value={targetAdminId || ''}
                  onChange={(e) => setTargetAdminId(e.target.value ? Number(e.target.value) : undefined)}
                  disabled={generating}
                  title="선택한 관리자의 프롬프트로 리포트 생성 및 해당 관리자의 예측글로 DB 저장"
                >
                  {adminList.map(admin => (
                    <option key={admin.admin_id} value={admin.admin_id}>
                      {admin.display_name} ({admin.role === 'administrator' ? '관리자' : '매니저'})
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>노션 페이지</label>
                <select
                  style={styles.select}
                  value={notionAdminId || ''}
                  onChange={(e) => setNotionAdminId(e.target.value ? Number(e.target.value) : undefined)}
                  disabled={generating}
                  title="선택한 관리자의 노션 페이지에 리포트 저장"
                >
                  {adminList.map(admin => (
                    <option key={admin.admin_id} value={admin.admin_id}>
                      {admin.display_name} ({admin.role === 'administrator' ? '관리자' : '매니저'})
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          <button
            style={{...styles.button, ...styles.primaryButton, alignSelf: 'flex-end'}}
            onClick={handleGeneratePredictions}
            disabled={generating}
          >
            {generating ? '생성중...' : '리포트 생성'}
          </button>
        </div>
      </div>

      {/* History Section */}
      <div style={styles.historySection}>
        <div style={styles.historyHeader}>
          <h3 style={styles.sectionTitle}>리포트 내역</h3>
        </div>

        <div style={styles.filters}>
          <select
            style={styles.filterSelect}
            value={filterSport}
            onChange={(e) => { setFilterSport(e.target.value); setReportsOffset(0); }}
          >
            <option value="">전체 종목</option>
            <option value="soccer">축구</option>
            <option value="baseball">야구</option>
            <option value="basketball">농구</option>
            <option value="volleyball">배구</option>
            <option value="hockey">하키</option>
          </select>
          <select
            style={styles.filterSelect}
            value={filterAiModel}
            onChange={(e) => { setFilterAiModel(e.target.value); setReportsOffset(0); }}
          >
            <option value="">전체 AI</option>
            <option value="gemini">Gemini</option>
            <option value="claude">Claude</option>
            <option value="openai">OpenAI</option>
          </select>
          {isAdministrator && adminList.length > 0 && (
            <select
              style={styles.filterSelect}
              value={showAllAdmins ? 'all' : (filterAdminId || '')}
              onChange={(e) => {
                if (e.target.value === 'all') {
                  setShowAllAdmins(true);
                  setFilterAdminId(undefined);
                } else {
                  setShowAllAdmins(false);
                  setFilterAdminId(e.target.value ? Number(e.target.value) : undefined);
                }
                setReportsOffset(0);
              }}
            >
              <option value="all">전체 관리자</option>
              {adminList.map(admin => (
                <option key={admin.admin_id} value={admin.admin_id}>
                  {admin.display_name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div style={styles.tableContainer}>
          {loadingReports ? (
            <div style={styles.emptyState}>로딩중...</div>
          ) : reports.length === 0 ? (
            <div style={styles.emptyState}>조회된 리포트가 없음.</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>경기일</th>
                  <th style={styles.th}>종목</th>
                  <th style={styles.th}>경기</th>
                  <th style={styles.th}>AI</th>
                  <th style={styles.th}>Ver</th>
                  {isAdministrator && showAllAdmins && <th style={styles.th}>프롬프트</th>}
                  {isAdministrator && showAllAdmins && <th style={styles.th}>노션</th>}
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr key={report.prediction_id}>
                    <td style={styles.td}>{formatMatchDate(report.match_date)}</td>
                    <td style={styles.td}>{getSportLabel(report.sport)}</td>
                    <td style={styles.tdMatch}>{report.home_team} vs {report.away_team}</td>
                    <td style={styles.td}>{report.ai_model.toUpperCase()}</td>
                    <td style={styles.td}>v{report.version}</td>
                    {isAdministrator && showAllAdmins && <td style={styles.td}>{report.admin_name || '-'}</td>}
                    {isAdministrator && showAllAdmins && <td style={styles.td}>{report.notion_admin_name || '-'}</td>}
                    <td style={styles.td}>
                      <button style={styles.detailButton} onClick={() => setSelectedReport(report)}>상세</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={styles.pagination}>
            <button
              style={{
                ...styles.paginationButton,
                ...(currentPage === 1 ? styles.paginationButtonDisabled : {})
              }}
              onClick={() => setReportsOffset(Math.max(0, reportsOffset - reportsLimit))}
              disabled={currentPage === 1}
            >
              이전
            </button>
            <span style={styles.paginationInfo}>{currentPage} / {totalPages}</span>
            <button
              style={{
                ...styles.paginationButton,
                ...(currentPage === totalPages ? styles.paginationButtonDisabled : {})
              }}
              onClick={() => setReportsOffset(reportsOffset + reportsLimit)}
              disabled={currentPage === totalPages}
            >
              다음
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedReport && (
        <div style={styles.modalBackdrop} onClick={() => setSelectedReport(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>{selectedReport.home_team} vs {selectedReport.away_team}</h3>
            <div style={styles.modalMeta}>
              {formatMatchDate(selectedReport.match_date)} | {getSportLabel(selectedReport.sport)} | {selectedReport.ai_model.toUpperCase()} v{selectedReport.version}
              {selectedReport.admin_name && ` | 프롬프트: ${selectedReport.admin_name}`}
              {selectedReport.notion_admin_name && ` | 노션: ${selectedReport.notion_admin_name}`}
            </div>
            <div style={styles.modalBody}>
              <pre style={styles.modalPre}>{selectedReport.full_report}</pre>
            </div>
            <div style={styles.modalFooter}>
              <button
                style={{...styles.button, ...styles.secondaryButton}}
                onClick={() => { navigator.clipboard.writeText(selectedReport.full_report); toast.success('복사됨'); }}
              >
                복사
              </button>
              <button style={{...styles.button, ...styles.secondaryButton}} onClick={() => setSelectedReport(null)}>닫기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    gap: spacing[5],
    overflow: 'hidden',
  },
  section: {
    ...commonStyles.section,
  },
  sectionTitle: {
    ...commonStyles.sectionTitle,
    margin: `0 0 ${spacing[3]} 0`,
  },
  formRow: {
    display: 'flex',
    gap: spacing[3],
    alignItems: 'flex-end',
    flexWrap: 'wrap' as const,
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[1],
    minWidth: '100px',
  },
  label: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    color: colors.gray[800],
  },
  input: commonStyles.input,
  select: commonStyles.select,
  button: commonStyles.button,
  primaryButton: commonStyles.buttonPrimary,
  secondaryButton: commonStyles.buttonSecondary,
  historySection: {
    ...commonStyles.section,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  historyHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing[2],
  },
  filters: {
    display: 'flex',
    gap: spacing[2],
    marginBottom: spacing[3],
    flexWrap: 'wrap' as const,
  },
  filterInput: {
    ...commonStyles.input,
    minWidth: '130px',
  },
  filterSelect: {
    ...commonStyles.select,
    minWidth: '100px',
  },
  tableContainer: {
    ...commonStyles.tableContainer,
    flex: 1,
    minHeight: 0,
  },
  table: commonStyles.table,
  th: commonStyles.th,
  td: commonStyles.td,
  tdMatch: {
    ...commonStyles.td,
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  detailButton: {
    ...commonStyles.button,
    ...commonStyles.buttonSecondary,
    padding: `${spacing[1]} ${spacing[4]}`,
    fontSize: typography.fontSize.sm,
  },
  pagination: commonStyles.paginationContainer,
  paginationButton: commonStyles.paginationButton,
  paginationButtonDisabled: commonStyles.paginationButtonDisabled,
  paginationInfo: commonStyles.paginationInfo,
  emptyState: commonStyles.emptyState,
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: colors.white,
    padding: spacing[8],
    maxWidth: '800px',
    maxHeight: '80vh',
    width: '90%',
    overflowY: 'auto',
    border: `1px solid ${colors.gray[200]}`,
    display: 'flex',
    flexDirection: 'column',
  },
  modalTitle: {
    ...commonStyles.sectionTitle,
    marginBottom: spacing[3],
  },
  modalMeta: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[600],
    marginBottom: spacing[4],
    padding: `${spacing[2]} ${spacing[3]}`,
    backgroundColor: colors.gray[50],
  },
  modalBody: {
    flex: 1,
    overflow: 'auto',
    marginBottom: spacing[4],
  },
  modalPre: {
    margin: 0,
    whiteSpace: 'pre-wrap',
    fontSize: typography.fontSize.sm,
    lineHeight: typography.lineHeight.relaxed,
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: spacing[3],
    borderTop: `1px solid ${colors.gray[200]}`,
    paddingTop: spacing[4],
  },
};

export default PredictionManager;
